import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, VStack, Text, Spinner, HStack, ScrollView, Progress } from 'native-base';
import { useDispatch } from 'react-redux';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { useDebounce } from 'src/hooks/useDebounce';
import { useImagePreloader } from 'src/hooks/useImagePreloader';
import { useDatasetData, useJobData, useImageData, useMappingData, useUploadProgress } from 'src/hooks/useLabelingSelectors';
import { fetchData, handleSaveProgress, handleCompleteLabeling } from 'src/components/ml/labelingUtils';
import { processImages } from 'src/components/ml/imageUtils';
import Pagination from 'src/components/Pagination';
import NoImagesAvailable from 'src/components/NoImagesAvailable';
import { fetchRejectionMapping, setUploadProgress, uploadComplete, clearDataset} from 'src/redux/actions/dataset';
import { IStore } from 'src/redux/reducers';
import ImageTable from 'src/components/ml/ImageTable';
import { IImageItem } from 'src/models/UploadImages';

const ITEMS_PER_PAGE = 26;

import { NavigationProp } from '@react-navigation/native';

interface CombinedLabelingPageProps {
    navigation: NavigationProp<any>;
}

const CombinedLabelingPage = ({ navigation }: CombinedLabelingPageProps) => {
    const dispatch = useDispatch();
    const isFocused = useIsFocused();

    // Use optimized selectors for better performance
    const { isJob, datasetId, jobId, isExistingDataset } = useDatasetData();
    const { jobData, jobDetails } = useJobData();
    const images = useImageData();
    const { rejectionMapping, grainId } = useMappingData();
    const { uploadProgress, uploadCompleted } = useUploadProgress();

    const [processedImages, setProcessedImages] = useState<IImageItem[]>([]);
    const [labelQueue, setLabelQueue] = useState({});
    const [changedLabels, setChangedLabels] = useState({});
    const debouncedLabels = useDebounce(labelQueue, 300);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [noImagesAvailable, setNoImagesAvailable] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Memoize sorted images to avoid reprocessing
    const sortedImages = useMemo(() => {
        if (!images || images.length === 0) return [];
        return [...images].sort((a, b) => {
            const pathA = a.imagePath || '';
            const pathB = b.imagePath || '';
            return pathA.localeCompare(pathB, undefined, { numeric: true, sensitivity: 'base' });
        });
    }, [images]);

    const fetchImagesData = useCallback(async () => {
        if (!isDataFetched) return;

        try {
            setErrorMessage(null); // Clear any previous error
            const newImages = await processImages(
                isJob,
                jobData ?? {},
                jobDetails,
                sortedImages,
                jobId ?? '',
                datasetId ?? '',
                isExistingDataset ?? false,
                isDataFetched,
            );

            if (newImages.length > 0) {
                setProcessedImages(newImages);
                setTotalPages(Math.ceil(newImages.length / ITEMS_PER_PAGE));
                setNoImagesAvailable(false);
                setChangedLabels({});
                setLabelQueue({});
            } else {
                setNoImagesAvailable(true);
                setErrorMessage('No images found for this job/dataset.');
            }
        } catch (error) {
            console.error('Error in fetchImagesData:', error);
            setNoImagesAvailable(true);
            const errorMsg = error instanceof Error ? error.message : 'Failed to load images. Please try again or contact support.';
            setErrorMessage(errorMsg);
        }
    }, [isJob, jobData, jobDetails, sortedImages, jobId, datasetId, isExistingDataset, isDataFetched]);

    useEffect(() => {
        if (isDataFetched && isFocused) {
            fetchImagesData();
        }
    }, [isDataFetched, fetchImagesData, isFocused]);

    useEffect(() => {
        if (!isFocused) {
          dispatch(setUploadProgress(0));
          dispatch(uploadComplete(false));
        }
      }, [isFocused, dispatch]);

    // Consolidated Navigation Logic
    useEffect(() => {
        if (uploadCompleted && uploadProgress === 100) {
          navigation.navigate('DatasetListingPage', { refresh: true });
        }
      }, [uploadCompleted, uploadProgress, navigation]);

    // Fetch dataset or job data when focused
    useFocusEffect(
        useCallback(() => {
            if (datasetId || jobId) {
                setIsDataFetched(false);
                setErrorMessage(null);
                dispatch(fetchRejectionMapping.request({ grainId: grainId ?? '' }));
                fetchData(dispatch, isJob, jobId ?? null, datasetId ?? null, isExistingDataset ?? false, setIsDataFetched);
            }
        }, [datasetId, jobId, dispatch, isJob, isExistingDataset, grainId])
    );

    useEffect(() => {
        setProcessedImages([]);
        setChangedLabels({});
        setLabelQueue({});
        setPage(0);
        setTotalPages(1);
        setNoImagesAvailable(false);
        setErrorMessage(null);
        setIsDataFetched(false);
    }, [datasetId, jobId]);

    // Debounced label changes for better performance
    const handleLabelChange = useCallback((imagePath: string, newLabel: string) => {
        setLabelQueue(prev => ({ ...prev, [imagePath]: newLabel }));
    }, []);

    // Apply debounced labels in batches
    useEffect(() => {
        if (Object.keys(debouncedLabels).length > 0) {
            setChangedLabels(prev => ({ ...prev, ...debouncedLabels }));
            setLabelQueue({});
        }
    }, [debouncedLabels]);

    const handleReturnToDashboard = useCallback(() => {
        // Clear dataset state to prevent reusing old dataset IDs
        dispatch(clearDataset());
        navigation.goBack();
    }, [dispatch, navigation]);

    // Use image preloader for better UX
    useImagePreloader(processedImages, page, ITEMS_PER_PAGE);

    // Paginate images with memoization
    const paginatedImages = useMemo(() => {
        return processedImages.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
    }, [page, processedImages]);

    if (!isFocused) {
        return null;
    }

    if (!datasetId && !jobId) {
        return <NoImagesAvailable navigation={navigation} />;
    }

    if (noImagesAvailable && (uploadProgress > 0)) {
        return (
            <Box flex={1} justifyContent="center" alignItems="center">
                <Spinner accessibilityLabel="Loading datasets" />
                <Text mt={4}>Loading images...</Text>
            </Box>
        );
    }

    return (
        <Box flex={1} p={5}>
            <ScrollView flex={1}> 
                {/* Move the ProgressBar here to always show it when uploading */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                    <Box mb={4}>
                        <HStack alignItems="center">
                            <Progress value={uploadProgress} flex={1} mr={2} />
                            <Text>{`${uploadProgress}%`}</Text>
                        </HStack>
                    </Box>
                )}
                <VStack space={2} flexGrow={1}>
                    <HStack justifyContent="space-between" alignItems="center">
                        <Text fontSize="xl" fontWeight="bold">Label Images</Text>
                        <Text>{isJob ? `Job ID: ${jobId}` : `Dataset ID: ${datasetId}`}</Text>
                    </HStack>
                    {!isDataFetched || processedImages.length === 0 ? (
                        <Box flex={1} justifyContent="center" alignItems="center">
                            <Spinner accessibilityLabel="Loading images" />
                            <Text fontSize="lg" fontWeight="bold" mt={2}>Loading...</Text>
                        </Box>
                    ) : paginatedImages.length > 0 ? (
                        <ImageTable
                            images={paginatedImages}
                            changedLabels={changedLabels}
                            rejectionMapping={rejectionMapping}
                            onLabelChange={handleLabelChange}
                            isExistingDataset={isExistingDataset}
                        />
                    ) : (
                        <Box flex={1} justifyContent="center" alignItems="center">
                            <Text fontSize="lg" fontWeight="bold" color="red.500" mb={2}>
                                {errorMessage || 'No images available'}
                            </Text>
                            {errorMessage && (
                                <Button 
                                    size="sm" 
                                    onPress={() => {
                                        setIsDataFetched(false);
                                        setErrorMessage(null);
                                        fetchData(dispatch, isJob, jobId ?? null, datasetId ?? null, isExistingDataset ?? false, setIsDataFetched);
                                    }}
                                >
                                    Retry
                                </Button>
                            )}
                        </Box>
                    )}
                    <Box mt={4}>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    </Box>
                    <HStack justifyContent="space-between" mt={4}>
                        <Button flex={1} onPress={handleReturnToDashboard}>
                            Return to Dashboard
                        </Button>
                        <Button flex={1} ml={2} onPress={() => handleCompleteLabeling(dispatch, isJob, isExistingDataset ?? false, jobId ?? '', datasetId ?? '', grainId ?? '', changedLabels, processedImages, navigation)}>
                            Complete Labeling
                        </Button>
                        <Button
                         flex={1} 
                         ml={2} 
                         onPress={async () => {
                             await handleSaveProgress(dispatch, isJob, isExistingDataset ?? false, jobId ?? '', datasetId ?? '', grainId ?? '', changedLabels, processedImages, navigation);
                             window.alert('Saved: Your progress has been saved successfully.');
                             setPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
                         }}
                         >
                            Save & Next
                        </Button>
                    </HStack>
                </VStack>
            </ScrollView>
        </Box>
    );
};

export default CombinedLabelingPage;